import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PropertyService } from '@app/_components/property';
import { first } from 'rxjs/operators';

interface DeleteStep {
	key: string;
	label: string;
	status: 'pending' | 'deleting' | 'done' | 'error' | 'skipped';
	countKeys: string[];
	errorMessage?: string;
}

@Component({
	selector: 'app-property-delete',
	standalone: false,
	templateUrl: './property-delete.component.html',
	styleUrls: ['./property-delete.component.css'],
	providers: [MessageService]
})
export class PropertyDeleteComponent implements OnInit {

	shortTermRentalId: number;
	preview: any = null;
	reservationDetails: any[] = [];
	warnings: string[] = [];
	loading = true;
	error: string = null;
	deleteBlocked = false;
	futureConfirmedReservations = 0;
	oneClickDeleting = false;

	deleteSteps: DeleteStep[] = [
		{ key: 'content', label: 'Content', status: 'pending', countKeys: ['pictures', 'reviews', 'ownerReviews', 'features', 'propertyFeatures'] },
		{ key: 'guests', label: 'Guests', status: 'pending', countKeys: ['guestBookMedia', 'guestBooks', 'guestFavorites', 'guestCommunicationStages', 'guestExitSurveys'] },
		{ key: 'operations', label: 'Operations', status: 'pending', countKeys: ['taskIntervals', 'taskUserJoins', 'taskDocumentJoins', 'taskDocuments', 'tasks', 'workLogs', 'accessCodes', 'lostFound', 'propertyConditions'] },
		{ key: 'financial', label: 'Financial', status: 'pending', countKeys: ['reportOwnerInvoiceDetails', 'reportOwnerInvoices', 'insuranceInvoices', 'generalLedgers', 'costProfiles', 'coupons', 'fees'] },
		{ key: 'rates', label: 'Rates', status: 'pending', countKeys: ['rateDetailLos', 'rateDetailLosHistory', 'rateGuests', 'rateBucketOverrides'] },
		{ key: 'rooms', label: 'Rooms & Channels', status: 'pending', countKeys: ['beds', 'bedrooms', 'bathrooms', 'channels', 'quoteDetails'] },
		{ key: 'joins', label: 'Join Tables', status: 'pending', countKeys: ['orderProductJoins', 'compendiumJoins', 'orderTaxJoins', 'maintenanceEvents'] },
		{ key: 'property-profiles', label: 'Property Profiles', status: 'pending', countKeys: ['propertyProfiles'] },
		{ key: 'reservations-and-orders', label: 'Reservations & Orders', status: 'pending', countKeys: ['reservations', 'orderDetails', 'orderDetailTaxes', 'orderInvoiceDetails', 'orderInvoices', 'guestCompanions', 'guestProfiles', 'yieldProfiles', 'contracts', 'reservationLedgers', 'vehicleCustomers', 'vehicleReservations', 'activityBookings', 'workOrderDetails', 'workOrders'] },
		{ key: 'property', label: 'Property Record', status: 'pending', countKeys: [] },
	];

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private propertyService: PropertyService,
		private confirmationService: ConfirmationService,
		private messageService: MessageService
	) { }

	ngOnInit(): void {
		this.shortTermRentalId = +this.route.snapshot.paramMap.get('shortTermRentalId');
		this.loadPreview();
	}

	loadPreview(): void {
		this.loading = true;
		this.error = null;
		this.propertyService.getDeletePreview(this.shortTermRentalId).pipe(first()).subscribe(
			res => {
				this.preview = res.data.deletePreview;
				this.warnings = this.preview.warnings || [];
				this.reservationDetails = this.preview.reservationDetails || [];
				this.deleteBlocked = this.preview.deleteBlocked || false;
				this.futureConfirmedReservations = this.preview.futureConfirmedReservations || 0;
				this.updateStepStatuses();
			},
			err => {
				this.error = err.error?.message || 'Failed to load delete preview.';
				this.messageService.add({ severity: 'error', summary: 'Error', detail: this.error, sticky: true });
			},
			() => { this.loading = false; }
		);
	}

	updateStepStatuses(): void {
		for (const step of this.deleteSteps) {
			if (step.status === 'done' || step.status === 'deleting') continue;
			if (step.key === 'property') continue;
			const count = this.getStepCount(step);
			if (count === 0) {
				step.status = 'skipped';
			}
		}
	}

	getStepCount(step: DeleteStep): number {
		if (!this.preview || !this.preview.childRecordCounts) return 0;
		const counts = this.preview.childRecordCounts;
		let total = 0;
		for (const key of step.countKeys) {
			total += (counts[key] || 0);
		}
		return total;
	}

	getTotalChildRecords(): number {
		if (!this.preview) return 0;
		return this.preview.totalChildRecords || 0;
	}

	canDeleteStep(index: number): boolean {
		if (this.deleteBlocked) return false;

		const step = this.deleteSteps[index];
		if (step.status === 'done' || step.status === 'skipped' || step.status === 'deleting') return false;

		// Property record (last step) can only be deleted when all prior steps are done/skipped
		if (step.key === 'property') {
			return this.deleteSteps.slice(0, -1).every(s => s.status === 'done' || s.status === 'skipped');
		}

		return step.status === 'pending' || step.status === 'error';
	}

	onDeleteStep(step: DeleteStep): void {
		const count = this.getStepCount(step);
		const message = step.key === 'property'
			? 'Are you sure you want to permanently delete the property record for ' + this.preview.propertyCode + '? This CANNOT be undone.'
			: 'Are you sure you want to delete ' + count + ' record(s) in "' + step.label + '" for property ' + this.preview.propertyCode + '?';

		this.confirmationService.confirm({
			message: message,
			header: 'Confirm Delete: ' + step.label,
			icon: 'pi pi-exclamation-triangle',
			acceptButtonStyleClass: 'p-button-danger',
			accept: () => {
				this.executeDeleteStep(step);
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete of "' + step.label + '" canceled.', life: 3000 });
			}
		});
	}

	executeDeleteStep(step: DeleteStep): void {
		step.status = 'deleting';
		step.errorMessage = null;
		this.propertyService.deleteCategory(this.shortTermRentalId, step.key).pipe(first()).subscribe(
			res => {
				step.status = 'done';
				this.messageService.add({
					severity: 'success',
					summary: 'Deleted!',
					detail: '"' + step.label + '" deleted successfully. ' + (res.data?.deleteResult?.totalDeleted || 0) + ' records removed.',
					life: 5000
				});

				// If the property record itself was deleted, navigate back
				if (step.key === 'property') {
					this.messageService.add({
						severity: 'success',
						summary: 'Property Deleted',
						detail: 'Property ' + this.preview.propertyCode + ' has been permanently deleted. Redirecting...',
						sticky: true
					});
					setTimeout(() => {
						this.router.navigate(['/property-list']);
					}, 3000);
				} else {
					// Reload preview to refresh counts
					this.reloadPreviewCounts();
				}
			},
			err => {
				step.status = 'error';
				step.errorMessage = err.error?.message || 'An error occurred.';
				this.messageService.add({
					severity: 'error',
					summary: 'Delete Failed',
					detail: '"' + step.label + '" failed: ' + step.errorMessage,
					sticky: true
				});
			}
		);
	}

	reloadPreviewCounts(): void {
		this.propertyService.getDeletePreview(this.shortTermRentalId).pipe(first()).subscribe(
			res => {
				this.preview = res.data.deletePreview;
				this.warnings = this.preview.warnings || [];
				this.reservationDetails = this.preview.reservationDetails || [];
				this.deleteBlocked = this.preview.deleteBlocked || false;
				this.futureConfirmedReservations = this.preview.futureConfirmedReservations || 0;
				this.updateStepStatuses();
			},
			err => {
				console.log('Failed to reload preview counts: ' + JSON.stringify(err));
			}
		);
	}

	onCancel(): void {
		this.router.navigate(['/property-list']);
	}

	onOneClickDelete(): void {
		if (this.deleteBlocked || this.oneClickDeleting || this.isAnyStepDeleting()) return;

		// First confirmation
		this.confirmationService.confirm({
			message: 'DELETE ' + this.preview.propertyCode + ' AND ALL ITS DATA? This will permanently remove '
				+ this.getTotalChildRecords() + ' child records plus the property itself. This cannot be undone.',
			header: 'Delete Property: ' + this.preview.propertyCode,
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Continue',
			rejectLabel: 'Cancel',
			acceptButtonStyleClass: 'p-button-danger',
			accept: () => {
				// Second confirmation
				this.confirmationService.confirm({
					message: 'Are you ABSOLUTELY SURE? Property ' + this.preview.propertyCode
						+ ' and all associated reservations, rates, pictures, ledgers, channels, and bookings will be permanently deleted.',
					header: 'Final Confirmation',
					icon: 'pi pi-exclamation-triangle',
					acceptLabel: 'Yes — Delete Now',
					rejectLabel: 'Cancel',
					acceptButtonStyleClass: 'p-button-danger',
					accept: () => {
						this.executeOneClickDelete();
					},
					reject: () => {
						this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'One-click delete canceled.', life: 3000 });
					}
				});
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'One-click delete canceled.', life: 3000 });
			}
		});
	}

	executeOneClickDelete(): void {
		this.oneClickDeleting = true;
		for (const step of this.deleteSteps) {
			if (step.status !== 'done' && step.status !== 'skipped') {
				step.status = 'deleting';
			}
		}

		this.propertyService.safeDelete(this.shortTermRentalId).pipe(first()).subscribe(
			res => {
				for (const step of this.deleteSteps) {
					step.status = 'done';
				}
				this.oneClickDeleting = false;
				this.messageService.add({
					severity: 'success',
					summary: 'Property Deleted',
					detail: 'Property ' + this.preview.propertyCode + ' has been permanently deleted. Redirecting...',
					sticky: true
				});
				setTimeout(() => {
					this.router.navigate(['/property-list']);
				}, 3000);
			},
			err => {
				this.oneClickDeleting = false;
				for (const step of this.deleteSteps) {
					if (step.status === 'deleting') step.status = 'error';
				}
				const msg = err.error?.message || err.message || 'One-click delete failed.';
				this.messageService.add({
					severity: 'error',
					summary: 'Delete Failed',
					detail: msg,
					sticky: true
				});
				this.reloadPreviewCounts();
			}
		);
	}

	getCompletedStepCount(): number {
		return this.deleteSteps.filter(s => s.status === 'done' || s.status === 'skipped').length;
	}

	isAnyStepDeleting(): boolean {
		return this.deleteSteps.some(s => s.status === 'deleting');
	}
}
